"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { format, parse, isValid } from "date-fns";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface InventoryTransfersDateFilterProps {
  fromDate: string;
  toDate: string;
}

function parseDate(value: string): Date | undefined {
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : undefined;
}

export function InventoryTransfersDateFilter({
  fromDate,
  toDate,
}: InventoryTransfersDateFilterProps) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>(() => ({
    from: parseDate(fromDate),
    to: parseDate(toDate),
  }));

  useEffect(() => {
    setRange({
      from: parseDate(fromDate),
      to: parseDate(toDate),
    });
  }, [fromDate, toDate]);

  const applyRange = () => {
    if (!range?.from || !isValid(range.from)) return;

    const from = format(range.from, "yyyy-MM-dd");
    const to = format(range.to ?? range.from, "yyyy-MM-dd");
    setOpen(false);

    const params = new URLSearchParams();
    params.set("fromDate", from);
    params.set("toDate", to);
    router.push(`/inventory-transfers?${params.toString()}`);
  };

  const label =
    range?.from && isValid(range.from)
      ? range.to && isValid(range.to)
        ? `${format(range.from, "PP")} – ${format(range.to, "PP")}`
        : format(range.from, "PP")
      : "Select date range";

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full sm:w-[320px] justify-start text-left font-normal",
              !range?.from && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {label}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={range}
            onSelect={setRange}
            numberOfMonths={2}
            initialFocus
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>
      <Button onClick={applyRange} disabled={!range?.from}>
        Apply
      </Button>
    </div>
  );
}
